'use client';

import { FC, useState, useMemo, useCallback } from 'react';
import { Box, Typography } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { StandardDialog } from '@/components/common/StandardDialog';
import { FormTextField, FormAutocomplete, FormDatePicker } from '@/components/common/FormComponents';
import { FormLabel } from '@/components/common/FormLabel';
import { OptionKey, KeyValueType } from '@/lib/options/optionTypes';
import { useSelectOptions } from '@/lib/options/useSelectOptions';
import { useBackendStore } from '@/stores/backendStore';
import { useSnackbar } from '@/hooks/useSnackbar';
import { RichTextEditor } from '@/components/common/RichTextEditor';
import { log } from '@/lib/utils/logger';
import { EntityAutocompleteWithLookup } from '@/components/common/EntityAutocompleteWithLookup';
import { useDialogStore, type BulkGrowthDialogResult } from '@/stores/dialogStore';
import { StatusDto } from '@/api/ApiClient';

import { useUserInteraction } from '@/hooks/useUnsavedChanges';
import { useAuthStore } from '@/stores/authStore';
import { LABEL_SUFFIX_OPTIONAL, LABEL_SUFFIX_REQUIRED } from '@/lib/formLabels';
import { canCreateWithoutProject } from '@/lib/auth/unassignedSampleAccess';
import type { ReportingCategoryDto } from '@/api/ApiClient';
import { getProblemDetail } from '@/lib/utils/apiErrors';
import { getBulkSubTechniques, buildTechniqueOptions } from '@/lib/utils/optionUtils';

const OPTION_KEYS: OptionKey[] = [
  'projects',
  'synthesisTechniques',
  'synthesisInstruments',
  'synthesisInstrumentOptions',
  'researcherOptions',
  'sampleReportingCategory',
];

/**
 * Dialog for creating a new bulk growth run
 * Based on NewBulkGrowthPopup from Knockout implementation
 */
export const NewBulkGrowthDialog: FC = () => {
  const { bulkGrowthDialog, closeBulkGrowthDialog } = useDialogStore();
  const backend = useBackendStore((state) => state.getBackend());
  const { options } = useSelectOptions(OPTION_KEYS);
  const { showSnackbar } = useSnackbar();
  const isAuthorized = useAuthStore((s) => s.isAuthorized);
  const allowCreateWithoutProject = canCreateWithoutProject(isAuthorized);
  const { showProjectSelectionDialog } = useDialogStore();

  const {
    samples,
    title,
    availableTechniques,
    availableInstrumentsGetter,
    initialProject,
    initialTechnique,
    validateBeforeSubmit,
  } = bulkGrowthDialog;
  const isOpen = bulkGrowthDialog.isOpen;

  const sourceSampleLabels = useMemo(
    () =>
      (samples ?? [])
        .map((s) => s.sampleLabel)
        .filter(Boolean)
        .join(', '),
    [samples]
  );

  const defaultTechnique = useMemo(() => {
    if (!availableTechniques?.length) return null;
    if (initialTechnique) {
      return availableTechniques.find((t) => t.id === initialTechnique) ?? null;
    }
    if (availableTechniques.length === 1) {
      return availableTechniques[0];
    }
    return null;
  }, [availableTechniques, initialTechnique]);

  const techniqueLocked = availableTechniques?.length === 1;

  // Form state
  const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());
  const [selectedProject, setSelectedProject] = useState<{ id: number; label: string } | null>(initialProject || null);
  const [selectedStaff, setSelectedStaff] = useState<KeyValueType | null>(() => {
    // Initialize with first researcher if available
    return options.researcherOptions?.[0] || null;
  });
  const [sampleLabel, setSampleLabel] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [selectedTechnique, setSelectedTechnique] = useState<{ id: string; name: string } | null>(defaultTechnique);
  const [selectedInstrument, setSelectedInstrument] = useState<{ id: string; name: string } | null>(null);
  const [selectedReportingCategory, setSelectedReportingCategory] = useState<ReportingCategoryDto | null>(null);
  const [showReportingCategory, setShowReportingCategory] = useState<boolean>(false);
  const [copying, setCopying] = useState<boolean>(false);

  // Error states
  const [projectError, setProjectError] = useState(false);
  const [staffError, setStaffError] = useState(false);
  const [techniqueError, setTechniqueError] = useState(false);
  const [instrumentError, setInstrumentError] = useState(false);

  const { hasUnsavedChanges, markAsInteracted } = useUserInteraction();

  // Check project type when user selects a project
  const handleProjectChange = useCallback(
    async (project: { id: number; label: string } | null) => {
      setSelectedProject(project);
      markAsInteracted();

      if (project?.id) {
        try {
          const projectData = await backend.project.get(project.id.toString());
          const isInternal = projectData.type?.isInternal ?? false;
          setShowReportingCategory(!isInternal);
          if (isInternal) {
            setSelectedReportingCategory(null);
          }
        } catch (error) {
          log.error('[NewBulkGrowthDialog] Failed to get project type:', error);
          setShowReportingCategory(false);
        }
      } else {
        setShowReportingCategory(false);
      }
    },
    [backend.project, markAsInteracted]
  );

  // Filter instruments based on selected technique
  const instrumentOptions = useMemo(() => {
    if (!selectedTechnique?.id) return [];

    if (availableInstrumentsGetter) {
      // Use custom getter if provided
      const customInstruments = availableInstrumentsGetter(selectedTechnique.id);
      // Convert to uniform format
      return customInstruments.map((inst) => ({
        id: inst.id,
        name: inst.name || inst.label || inst.value || inst.id,
      }));
    }

    // Default filtering based on synthesisTechniques
    const baseInstruments = options.synthesisInstruments ?? [];
    const formatted = options.synthesisInstrumentOptions ?? [];

    const filteredIds = new Set(
      baseInstruments.filter((inst) => inst.synthesisTechniques?.includes(selectedTechnique.id)).map((inst) => inst.id)
    );

    // Convert formatted options to uniform format
    return formatted
      .filter((opt) => filteredIds.has(opt.key))
      .map((opt) => ({
        id: opt.key,
        name: opt.value || opt.key,
      }));
  }, [selectedTechnique, availableInstrumentsGetter, options.synthesisInstruments, options.synthesisInstrumentOptions]);

  // Filter researchers to only those with Bulk technique
  const researcherOptions = useMemo(() => {
    // Note: In the original, it filters to researchers with Bulk technique
    // For now, return all researchers - may need to filter based on technique
    return options.researcherOptions ?? [];
  }, [options.researcherOptions]);

  // Project selection handler
  const handleProjectLookup = useCallback(async () => {
    const projects = await showProjectSelectionDialog('Select Project');
    if (projects && projects.length > 0) {
      const project = projects[0];
      await handleProjectChange({ id: project.id!, label: project.projectLabel! });
      setProjectError(false);
    }
  }, [showProjectSelectionDialog, handleProjectChange]);

  // Reset form and close
  const handleClose = useCallback(() => {
    // Clean up state when closing
    setSelectedDate(new Date());
    setSelectedProject(initialProject || null);
    setSelectedStaff(options.researcherOptions?.[0] || null);
    setSampleLabel('');
    setDescription('');
    setSelectedTechnique(defaultTechnique);
    setSelectedInstrument(null);
    setSelectedReportingCategory(null);
    setShowReportingCategory(false);
    setCopying(false);
    setProjectError(false);
    setStaffError(false);
    setTechniqueError(false);
    setInstrumentError(false);
    closeBulkGrowthDialog(null);
  }, [closeBulkGrowthDialog, initialProject, options.researcherOptions, defaultTechnique]);

  const handleSubmit = useCallback(async () => {
    let hasError = false;

    if (!allowCreateWithoutProject && !selectedProject) {
      setProjectError(true);
      hasError = true;
    } else {
      setProjectError(false);
    }

    if (!selectedStaff) {
      setStaffError(true);
      hasError = true;
    } else {
      setStaffError(false);
    }

    if (!selectedTechnique) {
      setTechniqueError(true);
      hasError = true;
    } else {
      setTechniqueError(false);
    }

    if (!selectedInstrument) {
      setInstrumentError(true);
      hasError = true;
    } else {
      setInstrumentError(false);
    }

    if (hasError) {
      showSnackbar('Please fill out all required fields.', 'error');
      return;
    }

    if (!selectedDate) {
      showSnackbar('Please select a date.', 'error');
      return;
    }

    if (!samples || samples.length === 0) {
      showSnackbar('No samples selected.', 'error');
      return;
    }

    if (
      !selectedInstrument ||
      (!allowCreateWithoutProject && !selectedProject) ||
      !selectedStaff ||
      !selectedTechnique ||
      !selectedDate
    ) {
      showSnackbar('Please fill out all required fields.', 'error');
      return;
    }

    try {
      const result: BulkGrowthDialogResult = {
        date: selectedDate,
        description: description || undefined,
        instrument: selectedInstrument.id,
        label: sampleLabel || undefined,
        project: selectedProject?.label ?? '',
        samples: samples.map((s) => s.id),
        staff: selectedStaff.key || selectedStaff.value || '',
        growthMethod: selectedTechnique.id,
        reportingCategory: selectedReportingCategory?.code || undefined,
      };

      if (validateBeforeSubmit) {
        const canProceed = await validateBeforeSubmit(result);
        if (!canProceed) {
          return;
        }
      }

      closeBulkGrowthDialog(result);
    } catch (error: unknown) {
      showSnackbar(getProblemDetail(error) ?? 'Failed to prepare bulk growth run.', 'error');
    }
  }, [
    selectedProject,
    selectedStaff,
    selectedTechnique,
    selectedInstrument,
    selectedDate,
    description,
    sampleLabel,
    samples,
    selectedReportingCategory,
    showSnackbar,
    closeBulkGrowthDialog,
    allowCreateWithoutProject,
    validateBeforeSubmit,
  ]);

  // Technique options - use provided techniques or all available
  const techniqueOptionsForDisplay = useMemo(() => {
    if (!samples || samples.length === 0) {
      return [];
    }
    if (availableTechniques) {
      return availableTechniques.filter((t) => t.id && t.name) as Array<{ id: string; name: string }>;
    }
    return buildTechniqueOptions(getBulkSubTechniques(options.synthesisTechniques));
  }, [availableTechniques, options.synthesisTechniques, samples]);

  if (!samples || samples.length === 0) {
    return null;
  }

  return (
    <StandardDialog
      open={isOpen}
      onClose={handleClose}
      title={title || 'Start new Growth Run'}
      maxWidth="md"
      onSubmit={handleSubmit}
      submitText="Create Instrument Run"
      hasUnsavedChanges={hasUnsavedChanges}
      unsavedChangesMessage="You have unsaved changes. Are you sure you want to discard them?"
    >
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        {/* Project */}
        <Box display="flex" gap={2} mb={2}>
          <Box flex={1}>
            <EntityAutocompleteWithLookup
              label={`Project ID${allowCreateWithoutProject ? LABEL_SUFFIX_OPTIONAL : LABEL_SUFFIX_REQUIRED}`}
              options={
                options.projects
                  ?.filter((p) => p.id && p.projectLabel && p.status === StatusDto.Submitted)
                  ?.map((p) => ({ id: p.id!, label: p.projectLabel! })) ?? []
              }
              value={selectedProject}
              onChange={(newValue) => {
                const next = newValue as { id: number; label: string } | null;
                void handleProjectChange(next);
                if (next) setProjectError(false);
              }}
              multiple={false}
              onLookupClick={handleProjectLookup}
              placeholder={allowCreateWithoutProject ? 'No project' : undefined}
              error={projectError}
              helperText={projectError ? 'Project is required.' : undefined}
              lookupTooltip="Search and select project"
            />
          </Box>
          {showReportingCategory ? (
            <Box flex={1}>
              <FormAutocomplete
                options={options.sampleReportingCategory ?? []}
                getOptionLabel={(option) => option?.label || option?.code || ''}
                value={selectedReportingCategory}
                onChange={(_, newValue) => {
                  setSelectedReportingCategory(newValue);
                  markAsInteracted();
                }}
                label="Reporting Category"
              />
            </Box>
          ) : null}
        </Box>

        {/* Created by and Date */}
        <Box display="flex" gap={2} mb={2}>
          <Box flex={1}>
            <FormAutocomplete
              options={researcherOptions}
              getOptionLabel={(option) => option?.value || ''}
              value={selectedStaff}
              onChange={(_, newValue) => {
                setSelectedStaff(newValue);
                markAsInteracted();
              }}
              label="Created by"
              required
              error={staffError}
            />
          </Box>
          <Box flex={1}>
            <FormDatePicker
              label="Date"
              value={selectedDate}
              onChange={(newValue) => {
                setSelectedDate(newValue);
                markAsInteracted();
              }}
              required
            />
          </Box>
        </Box>

        {/* Source sample(s) */}
        {sourceSampleLabels ? (
          <Box mb={2}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Source sample(s)
            </Typography>
            <Typography variant="body1">{sourceSampleLabels}</Typography>
          </Box>
        ) : null}

        {/* Sample ID (new growth run label) */}
        <Box mb={2}>
          <FormTextField
            label="Sample ID"
            value={sampleLabel}
            onChange={(e) => {
              setSampleLabel(e.target.value);
              markAsInteracted();
            }}
            placeholder="<will be generated>"
            fullWidth
          />
        </Box>

        {/* Method and Instrument */}
        <Box display="flex" gap={2} mb={2}>
          <Box flex={1}>
            <FormAutocomplete
              options={techniqueOptionsForDisplay}
              getOptionLabel={(option) => option?.name || option?.id || ''}
              value={selectedTechnique}
              onChange={(_, newValue) => {
                setSelectedTechnique(newValue);
                // Reset instrument when technique changes
                setSelectedInstrument(null);
                markAsInteracted();
              }}
              label="Method"
              required
              error={techniqueError}
              disabled={copying || techniqueLocked}
            />
          </Box>
          <Box flex={1}>
            <FormAutocomplete
              options={instrumentOptions}
              getOptionLabel={(option) => option?.name || option?.id || ''}
              value={selectedInstrument}
              onChange={(_, newValue) => {
                setSelectedInstrument(newValue);
                markAsInteracted();
              }}
              label="Target Instrument"
              required
              error={instrumentError}
            />
          </Box>
        </Box>

        {/* Description */}
        <Box mb={2}>
          <Typography variant="subtitle2" gutterBottom>
            <FormLabel optional>Description</FormLabel>
          </Typography>
          <RichTextEditor
            value={description}
            onChange={(value) => {
              setDescription(value);
              markAsInteracted();
            }}
          />
        </Box>
      </LocalizationProvider>
    </StandardDialog>
  );
};
